import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Crown from "@/components/Crown";
import SectionLabel from "@/components/SectionLabel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MIRROR_INSIDE = [
  {
    h: "The Mirror Method Diagnostic",
    p: "Eight statements. Score yourself. Name what’s misaligned.",
  },
  {
    h: "Five Questions That Surface What You Actually Want",
    p: "Bypass the version of you that answers based on what’s expected.",
  },
  {
    h: "A Reframe for the Woman You’re Becoming",
    p: "Old story → new truth. Identity reconstruction, not affirmations.",
  },
  {
    h: "The Daily Ritual to Rebuild Self-Trust",
    p: "Witness. Decide. Keep. Record. Ten minutes. Every morning.",
  },
  {
    h: "Bonus: The Mirror Method GPT",
    p: "A strategic thinking partner to walk you through every page.",
  },
];

const FreebieInline = ({ variant = "section" }) => {
  const [first, setFirst] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!first.trim() || !email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads/freebie`, {
        first_name: first.trim(),
        email: email.trim(),
      });
      setDone(true);
      toast.success("Your guide is on its way — opening it now.");
      const url = `${API}/freebie/download`;
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      a.click();
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // When used on Home: we render the full gold band wrapper.
  // When used on Freebie page hero: we render only the form/card pair (no outer wrapper).
  const formAndCard = (
    <div className="freebie-grid">
      <div>
        <Crown size={36} opacity={0.95} />
        <SectionLabel>FREE DOWNLOAD</SectionLabel>
        <h2 className="section-title" style={{ color: "var(--dark)", maxWidth: 520 }}>
          The Mirror <span className="italic-c">Method.</span>
        </h2>
        <p className="body-base" style={{ color: "rgba(13,31,36,.78)", maxWidth: 480 }}>
          A free guide for high-performing women who are ready to stop performing and start telling
          the truth. The Mirror Method helps you see yourself clearly — maybe for the first time
          in a long time.
        </p>
        {done ? (
          <div className="form-row" data-testid="freebie-success">
            <p style={{ fontSize: 15, color: "var(--dark)", fontWeight: 500 }}>
              ✓ It’s yours. The download just opened in a new tab — and we’ve sent it to {email}.
            </p>
            <a
              href={`${API}/freebie/download`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-teal"
              style={{ alignSelf: "flex-start" }}
              data-testid="freebie-download-again"
            >
              Download Again →
            </a>
          </div>
        ) : (
          <form className="form-row" onSubmit={submit} data-testid="freebie-form">
            <input
              className="input-base"
              placeholder="First name"
              value={first}
              onChange={(e) => setFirst(e.target.value)}
              required
              data-testid="freebie-input-first"
            />
            <input
              className="input-base"
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="freebie-input-email"
            />
            <button className="btn btn-teal" type="submit" disabled={loading} data-testid="freebie-submit">
              {loading ? "Sending…" : "Send Me the Free Guide →"}
            </button>
            <p className="form-note">No spam, ever. Unsubscribe anytime.</p>
          </form>
        )}
      </div>

      <div className="fcard-dark" data-testid="freebie-card">
        <SectionLabel gold>WHAT’S INSIDE</SectionLabel>
        <h3
          className="h-display"
          style={{ fontSize: 28, color: "var(--gold)", margin: "10px 0 18px" }}
        >
          What’s inside the Mirror Method
        </h3>
        <ul>
          {MIRROR_INSIDE.map((item, i) => (
            <li key={i}>
              <strong style={{ color: "var(--cream)", display: "block", marginBottom: 4 }}>
                {item.h}
              </strong>
              <span style={{ color: "rgba(253,248,245,.85)", fontSize: 13 }}>
                — {item.p}
              </span>
            </li>
          ))}
        </ul>
        <p style={{ fontSize: 12, color: "rgba(253,248,245,.5)", marginTop: 18 }}>
          Delivered as a PDF. Roughly a 25-minute read, a lifetime to use.
        </p>
      </div>
    </div>
  );

  if (variant === "bare") {
    return formAndCard;
  }

  return (
    <section className="freebie-band" data-testid="freebie-inline">
      <div className="container-x">{formAndCard}</div>
    </section>
  );
};

export default FreebieInline;
